/**
 * Network-level (fire-and-forget) сохранение генераций в историю.
 *
 * Проблема старой архитектуры: сохранение в историю было привязано к
 * React state жизненного цикла `GenerateProvider` — через
 * `genSnapshotRef` + useEffect на `flow.result`. Это означало, что
 * save зависел от:
 *   - Компонент должен быть смонтирован в нужный момент
 *   - `flow.result` должно обновиться через setResult
 *   - Snapshot должен был быть захвачен правильно
 *
 * При навигации пользователя по страницам (особенно мимо /bots/* /generate)
 * или при reload страницы во время генерации — save терялся.
 *
 * Новая архитектура: snapshot генерации пишется в localStorage в момент
 * СТАРТА генерации (ещё до сетевого запроса). Source image сохраняется
 * в IndexedDB upfront. Когда backend возвращает результат — мы читаем
 * snapshot из localStorage и вызываем `addGeneration(...)` напрямую.
 *
 * Две точки входа:
 *   1. `useGenerationFlow.handleGenerate` — синхронный success path
 *      (когда runGeneration резолвится в текущей сессии)
 *   2. `HistoryPersistenceBridge` — слушает `globalGen.status === 'done'`
 *      и подхватывает результат, если handleGenerate-промис не отработал
 *      (например, юзер перезагрузил страницу во время генерации)
 *
 * Оба пути используют один и тот же `saveToHistory` helper.
 */
import type { GenerationCard } from '../types/generationCard'
import { saveSourceImage } from '../utils/sourceImageDb'
import { knownAssetNames } from '../api/assets'
import { assetTokensToPlain } from '../utils/assetMentions'
import {
	PENDING_GENERATION_KEY,
	GENERATE_RESULT_KEY,
} from '../pages/generate/utils/constants'

// ── Типы ──────────────────────────────────────────────────────

/**
 * Snapshot — всё что нужно чтобы сохранить генерацию в историю,
 * даже если React state провайдера уже не актуален.
 * JSON-сериализуется в `PENDING_GENERATION_KEY`.
 */
/**
 * Где генерацию запустили. Нужен стикеру прогресса: клик возвращает человека
 * ровно на ту страницу и в тот инструмент, откуда он начал, а не в общий
 * раздел. Без этого снимок знает только модель — по ней нельзя ни подписать
 * стикер названием карточки, ни восстановить tool-режим на возврате
 * (жалоба владельца 2026-09-21: ушёл со страницы во время генерации, вернулся —
 * обычный виджет, и карточка не показывает, что по ней шла работа).
 */
export interface GenerationOrigin {
	/** Путь в момент старта, с query: лендинг карточки, раздел или /generate. */
	path: string
	/** id карточки-инструмента, если генерация шла из неё (пин `AttachedStyleCard.id`). */
	cardId?: string
	/** Название карточки — подпись стикера. */
	cardTitle?: string
	/** Обложка карточки, если есть: стикер показывает её, пока нет результата. */
	cardPreviewUrl?: string
}

export interface GenerationSnapshot {
	/** UUID генерации — используется как id в истории. */
	generationId: string
	/**
	 * Backend task id. Ставится когда runGeneration делает poll
	 * (через `onPendingSave` callback). Для синхронных генераций
	 * может остаться undefined — тогда save пойдёт по handleGenerate path.
	 */
	taskId?: string
	/** Unix ms — для TTL-чеков на mount'е app'а. */
	startedAt: number
	// ── Метаданные генерации ─────────────────────────
	prompt: string
	kind: 'image' | 'video' | 'audio' | 'presentation'
	isVideo: boolean
	modelId: string
	modelName: string
	settings: {
		aspectRatio?: string
		resolution?: string
		duration?: string
		outputFormat?: string
	}
	/** Ключ в IndexedDB, по которому лежит source image (если был загружен). */
	sourceImageDbKey?: string
	/** Откуда запустили — адрес возврата для стикера прогресса. */
	origin?: GenerationOrigin
	// ── Audio (Suno) специфика ───────────────────────
	audioTitle?: string
	audioStyle?: string
	audioInstrumental?: boolean
	audioVocalGender?: string
	audioCustomMode?: boolean
	// ── Presentation специфика ───────────────────────
	/** 'edit' vs 'create' — определяет имя файла при скачивании. */
	presentationMode?: 'edit' | 'create'
}

// ── Storage helpers ──────────────────────────────────────────

/**
 * Пишет snapshot в `PENDING_GENERATION_KEY`. Вызывается в момент
 * старта генерации (перед `runGeneration`). На ошибках localStorage
 * (quota exceeded и т.д.) — молча игнорим, save будет потерян но
 * генерация продолжится.
 */
export function savePendingSnapshot(snapshot: GenerationSnapshot): void {
	try {
		localStorage.setItem(PENDING_GENERATION_KEY, JSON.stringify(snapshot))
	} catch {
		// ignore
	}
}

/**
 * Читает snapshot из localStorage. Возвращает null если нет или повреждён.
 */
export function loadPendingSnapshot(): GenerationSnapshot | null {
	try {
		const raw = localStorage.getItem(PENDING_GENERATION_KEY)
		if (!raw) return null
		const parsed = JSON.parse(raw)
		// Минимальная валидация — достаточно полей которые нужны resolve/save.
		if (
			typeof parsed === 'object' &&
			parsed !== null &&
			typeof parsed.generationId === 'string' &&
			typeof parsed.modelId === 'string'
		) {
			return parsed as GenerationSnapshot
		}
		return null
	} catch {
		return null
	}
}

/**
 * Обновляет taskId (и опционально modelName) в существующем snapshot.
 * Вызывается из `useGenerationFlow.savePending` когда backend
 * возвращает taskId. Если snapshot не существует — no-op.
 */
export function updatePendingTaskId(taskId: string, modelName?: string): void {
	const existing = loadPendingSnapshot()
	if (!existing) return
	existing.taskId = taskId
	if (modelName) existing.modelName = modelName
	try {
		localStorage.setItem(PENDING_GENERATION_KEY, JSON.stringify(existing))
	} catch {
		// ignore
	}
}

/**
 * Удаляет snapshot. Вызывается после успешного save или при expired.
 */
export function clearPendingSnapshot(): void {
	try {
		localStorage.removeItem(PENDING_GENERATION_KEY)
	} catch {
		// ignore
	}
}

// ── Source image persistence ─────────────────────────────────

/**
 * Сохраняет source image в IndexedDB перед стартом генерации.
 * Возвращает ключ (равный generationId) или undefined если не получилось
 * или sourceFile = null.
 *
 * Ключ такой же как `snapshot.generationId` — связываем запись в истории
 * с её исходником напрямую, без дополнительного маппинга.
 */
export async function persistSourceImage(
	generationId: string,
	sourceFile: File | null,
): Promise<string | undefined> {
	if (!sourceFile) return undefined
	try {
		await saveSourceImage(generationId, sourceFile)
		return generationId
	} catch {
		// IndexedDB недоступен — запись сохранится без исходника
		return undefined
	}
}

// ── The actual save call ─────────────────────────────────────

/**
 * Чистит presigned-параметры из URL чтобы не протухало.
 */
function cleanPresignedUrl(url: string): string {
	try {
		const u = new URL(url)
		if (u.searchParams.has('X-Amz-Signature')) {
			return u.origin + u.pathname
		}
	} catch {
		// внешний URL — оставляем как есть
	}
	return url
}

/**
 * Главная функция — сохраняет генерацию в историю на основе snapshot'а
 * и результата. Вызывается из двух мест:
 *
 *   1. `useGenerationFlow.handleGenerate` success path — сразу когда
 *      runGeneration вернул результат
 *   2. `HistoryPersistenceBridge` — когда `globalGen.status` стал 'done'
 *      и snapshot ещё в localStorage (резюм после reload)
 *
 * Идемпотентна: `addGeneration` внутри сам дедуплицирует по id/previewUrl,
 * так что двойной вызов безопасен.
 */
export function saveToHistory({
	resultUrl,
	resultUrls,
	imageUrls,
	snapshot,
	addGeneration,
	requestId,
}: {
	resultUrl: string
	resultUrls?: string[] | null
	imageUrls?: string[] | null
	snapshot: GenerationSnapshot
	addGeneration: (
		gen: Omit<GenerationCard, 'id' | 'createdAt' | 'type'> & { id?: string },
	) => void
	/** Backend request id — оценка 👍/👎 на карточке истории привязывается к
	 *  нему. Синхронный путь передаёт его из ответа генерации; polling-путь
	 *  не передаёт вовсе — берём snapshot.taskId (это тот же request id). */
	requestId?: string | null
}): void {
	const cleanUrl = cleanPresignedUrl(resultUrl)

	const audioExtras =
		snapshot.kind === 'audio'
			? {
					audioTracks: resultUrls || [cleanUrl],
					audioTitle: snapshot.audioTitle,
					audioStyle: snapshot.audioStyle,
					audioInstrumental: snapshot.audioInstrumental,
					audioVocalGender: snapshot.audioVocalGender,
					audioCustomMode: snapshot.audioCustomMode,
				}
			: {}

	const presentationExtras =
		snapshot.kind === 'presentation'
			? {
					pptxUrl: cleanUrl,
					presentationMode: snapshot.presentationMode,
				}
			: {}

	// Для аудио previewUrl — обложка (image_url от Suno), если пришла.
	// Для presentation — пусто (PPTX не превьюшится в браузере, рендерим
	// иконку через ветку в recent-list / CardModal по kind).
	// Для image/video — чистый URL результата.
	let previewUrl: string
	if (snapshot.kind === 'audio') {
		previewUrl = imageUrls && imageUrls[0] ? imageUrls[0] : ''
	} else if (snapshot.kind === 'presentation') {
		previewUrl = ''
	} else {
		previewUrl = cleanUrl
	}

	addGeneration({
		id: snapshot.generationId,
		previewUrl,
		sourceImageDbKey: snapshot.sourceImageDbKey,
		prompt: snapshot.prompt,
		isVideo: snapshot.isVideo,
		kind: snapshot.kind,
		modelId: snapshot.modelId,
		modelName: snapshot.modelName,
		settings: snapshot.settings,
		requestId: requestId ?? snapshot.taskId ?? undefined,
		...audioExtras,
		...presentationExtras,
	})
}

// ── Snapshot builder ─────────────────────────────────────────

export interface BuildSnapshotArgs {
	prompt: string
	isVideo: boolean
	isAudio: boolean
	/** category === 'presentation' (Umnik Slides). Добавлено в Phase 1 — чтобы
	 *  PPTX-генерации записывались в recent history с правильным kind. */
	isPresentation?: boolean
	modelId: string
	modelName: string
	settings: GenerationSnapshot['settings']
	sourceImageDbKey?: string
	origin?: GenerationOrigin
	// Аудио
	audioTitle?: string
	audioStyle?: string
	audioInstrumental?: boolean
	audioVocalGender?: string
	audioCustomMode?: boolean
	// Presentation
	presentationMode?: 'edit' | 'create'
}

/**
 * Создаёт новый GenerationSnapshot с фреш generationId и startedAt.
 */
export function buildSnapshot(args: BuildSnapshotArgs): GenerationSnapshot {
	const generationId = `${Date.now()}-${Math.random().toString(36).slice(2)}`
	const kind: GenerationSnapshot['kind'] = args.isPresentation
		? 'presentation'
		: args.isAudio
			? 'audio'
			: args.isVideo
				? 'video'
				: 'image'
	return {
		generationId,
		startedAt: Date.now(),
		// Упоминания ассетов (@{uuid}@) человеку показываем именами — как промпт запишет сервер.
		prompt: assetTokensToPlain(args.prompt, knownAssetNames()),
		kind,
		isVideo: args.isVideo,
		modelId: args.modelId,
		modelName: args.modelName,
		settings: args.settings,
		sourceImageDbKey: args.sourceImageDbKey,
		origin: args.origin,
		audioTitle: args.audioTitle,
		audioStyle: args.audioStyle,
		audioInstrumental: args.audioInstrumental,
		audioVocalGender: args.audioVocalGender,
		audioCustomMode: args.audioCustomMode,
		presentationMode: args.presentationMode,
	}
}

// ── Widget result persistence (между reload и навигацией) ────
//
// Храним последний завершённый результат виджета per-категория
// (image/video/audio). Юзер переключается между категориями — каждая
// помнит свой последний результат. Очищается только по явной очистке
// виджета пользователем. Restore происходит при mount useGenerationFlow.
//
// Отличие от PENDING_GENERATION_KEY: тот хранит снимок для сохранения
// в историю (pending → запустили → записали). Тут — просто живой
// widget state, уже завершённый результат, для отображения в .gw.

export type GenerateResultCategory = 'image' | 'video' | 'audio' | 'presentation'

export interface PersistedGenerateResult {
	savedAt: number
	result: string
	resultUrls: string[] | null
	resultImageUrls: string[] | null
	lastRequestId: string | null
	modelId: string
	userRating: number | null
}

/** Map category → snapshot. Хранится в одном localStorage-ключе. */
type PersistedResultMap = Partial<Record<GenerateResultCategory, PersistedGenerateResult>>

function readMap(): PersistedResultMap {
	try {
		const raw = localStorage.getItem(GENERATE_RESULT_KEY)
		if (!raw) return {}
		const parsed = JSON.parse(raw)
		if (typeof parsed !== 'object' || parsed === null) return {}
		// Старый формат (до per-category): единый snapshot с modelId.
		// Если встретили — никуда не перекладываем, просто игнорим и
		// перезапишется при следующем save. Это консервативнее чем
		// пытаться угадать категорию из modelId на лету.
		if (typeof (parsed as PersistedGenerateResult).result === 'string') {
			return {}
		}
		return parsed as PersistedResultMap
	} catch {
		return {}
	}
}

function writeMap(map: PersistedResultMap): void {
	try {
		localStorage.setItem(GENERATE_RESULT_KEY, JSON.stringify(map))
	} catch {
		// ignore quota etc.
	}
}

export function saveGenerateResult(
	category: GenerateResultCategory,
	snapshot: PersistedGenerateResult,
): void {
	const map = readMap()
	map[category] = snapshot
	writeMap(map)
}

export function loadGenerateResult(
	category: GenerateResultCategory,
): PersistedGenerateResult | null {
	const map = readMap()
	return map[category] ?? null
}

/** Очистка результата. Без аргумента — чистим всё; с category — только её. */
export function clearGenerateResult(category?: GenerateResultCategory): void {
	if (!category) {
		try { localStorage.removeItem(GENERATE_RESULT_KEY) } catch { /* приватный режим — некритично */ }
		return
	}
	const map = readMap()
	delete map[category]
	writeMap(map)
}
